import {PropertyType} from 'common/report/constants';
import {
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  RetentionAlignmentType,
  TopLevelMetricType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {FilterClause} from 'common/report/insights/util/types';
import {
  ListOperator,
  NthTimeOperator,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {MPProperties} from 'common/constants';
import {ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {PredefinedCohort} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {ICON_MAPPING} from 'common/query-builder/query-entry-section/util/entry-icon';

import {DateRangeType, TimeUnit} from '../date-range-picker/models/types';
import {commonInsightsBookmarkParams, dailyIntervalLast30Days, weeklyIntervalLast3Months} from './constants';

const dataGroupFilter = (dataGroupId: string, distinctId: string): FilterClause[] => [
  {
    dataGroupId,
    dataset: `$mixpanel`,
    defaultType: PropertyType.String,
    filterOperator: StringOperator.Equals,
    filterType: PropertyType.String,
    filterValue: [distinctId],
    profileType: null,
    propertyObjectKey: undefined,
    resourceType: InsightsResourceType.People,
    search: ``,
    value: MPProperties.DistinctId,
  },
];

export const xauBookmark = ({
  xauType,
  dataGroupId,
  distinctId,
  timeParams,
}: {
  xauType: XAU_MATH_TYPES;
  dataGroupId: string;
  distinctId: string;
  timeParams?: Array<{dateRangeType: DateRangeType; unit: TimeUnit; window: {unit: TimeUnit; value: number}}>;
}): InsightsBookmarkParams => ({
  ...commonInsightsBookmarkParams,
  sections: {
    cohorts: [],
    filter: dataGroupFilter(dataGroupId, distinctId),
    formula: [],
    group: [],
    show: [
      {
        behavior: {
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          filters: [],
          name: SPECIAL_EVENTS.ALL_EVENTS,
          profileType: undefined,
          resourceType: InsightsResourceType.Events,
          search: ``,
          type: MetricType.Event,
        },
        display: {},
        measurement: {
          cumulative: false,
          math: xauType,
          perUserAggregation: undefined,
          property: undefined,
          rolling: undefined,
          segmentMethod: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
    ],
    time: timeParams ? timeParams : dailyIntervalLast30Days,
  },
});

export const weeklyEventsPerUserBookmark = (dataGroupId: string, distinctId: string): InsightsBookmarkParams => ({
  ...commonInsightsBookmarkParams,
  sections: {
    cohorts: [],
    filter: dataGroupFilter(dataGroupId, distinctId),
    formula: [],
    group: [],
    show: [
      {
        isHidden: true,
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          filters: [],
          name: SPECIAL_EVENTS.ALL_EVENTS,
          profileType: undefined,
          search: ``,
        },
        display: {},
        measurement: {
          math: PRIMITIVE_MATH_TYPES.TOTAL,
          perUserAggregation: undefined,
          rolling: undefined,
          cumulative: false,
          property: undefined,
          segmentMethod: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
      {
        isHidden: true,
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          filters: [],
          name: SPECIAL_EVENTS.ALL_EVENTS,
          profileType: undefined,
          search: ``,
        },
        display: {},
        measurement: {
          math: PRIMITIVE_MATH_TYPES.UNIQUE,
          perUserAggregation: undefined,
          rolling: undefined,
          cumulative: false,
          property: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
      {
        isExpanded: true,
        display: {},
        name: `Formula`,
        definition: `A/B`,
        measurement: {},
        type: TopLevelMetricType.Formula,
      },
    ],
    time: weeklyIntervalLast3Months,
  },
});

const anyEventCommonBehaviorParams = {
  aggregationOperator: `total`,
  aggregationOperatorPerUser: undefined,
  event: {
    value: SPECIAL_EVENTS.ANY_EVENT,
    label: `Any event`,
    isRecentlyUsed: false,
  },
  filters: [],
  filtersOperator: `and`,
  behavioralFiltersOperator: `and`,
  property: undefined,
};

const resurrectedCustomPropertyCommonParams = {
  customPropertyId: undefined,
  dataGroupId: undefined,
  tempDataGroupId: undefined,
  resourceType: ResourceType.User,
  propertyName: undefined,
  propertyObjectKey: undefined,
  propertyDefaultType: PropertyType.Number,
  propertyType: PropertyType.Number,
};

export const weeklyResurrectedUserBookmark = (dataGroupId: string, distinctId: string): InsightsBookmarkParams => ({
  ...commonInsightsBookmarkParams,
  sections: {
    cohorts: [],
    filter: dataGroupFilter(dataGroupId, distinctId),
    formula: [],
    group: [],
    show: [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          filters: [
            {
              dataset: `$mixpanel`,
              value: `$cohorts`,
              resourceType: InsightsResourceType.Events,
              profileType: null,
              search: ``,
              dataGroupId: undefined,
              filterType: PropertyType.List,
              filterOperator: ListOperator.Contains,
              filterValue: [
                {
                  cohort: {
                    raw_cohort: {
                      name: `resurrected users`,
                      id: undefined,
                      groups: [
                        {
                          type: `cohort_group`,
                          event: {
                            resourceType: ResourceType.Cohort,
                            value: PredefinedCohort.AllUsers,
                          },
                          filters: [
                            {
                              customProperty: {
                                name: ``,
                                description: ``,
                                behavior: {
                                  ...anyEventCommonBehaviorParams,
                                  dateRange: {
                                    type: DateRangeType.RelativeAfter,
                                    window: {
                                      unit: TimeUnit.Day,
                                      value: 30,
                                    },
                                  },
                                },
                              },
                              ...resurrectedCustomPropertyCommonParams,
                              propertyType: PropertyType.Number,
                              filterOperator: NumberOperator.AtLeast,
                              filterValue: 1,
                            },
                            {
                              customProperty: {
                                name: ``,
                                description: ``,
                                behavior: {
                                  ...anyEventCommonBehaviorParams,
                                  dateRange: {
                                    type: `between`,
                                    from: {
                                      unit: TimeUnit.Day,
                                      value: 90,
                                    },
                                    to: {
                                      value: 60,
                                      unit: TimeUnit.Day,
                                    },
                                    rolling: true,
                                  },
                                },
                              },
                              ...resurrectedCustomPropertyCommonParams,
                              filterOperator: NumberOperator.AtLeast,
                              filterValue: 1,
                            },
                            {
                              customProperty: {
                                name: ``,
                                description: ``,
                                behavior: {
                                  ...anyEventCommonBehaviorParams,
                                  dateRange: {
                                    type: `between`,
                                    from: {
                                      unit: TimeUnit.Day,
                                      value: 60,
                                    },
                                    to: {
                                      value: 30,
                                      unit: TimeUnit.Day,
                                    },
                                    rolling: true,
                                  },
                                },
                              },
                              ...resurrectedCustomPropertyCommonParams,
                              filterOperator: NumberOperator.EqualTo,
                              filterValue: 0,
                            },
                          ],
                          filtersOperator: `and`,
                          behavioralFiltersOperator: `and`,
                          groupingOperator: undefined,
                          property: undefined,
                        },
                      ],
                    },
                    negated: false,
                    name: `resurrected users`,
                  },
                },
              ],
              propertyObjectKey: undefined,
              determiner: `all`,
            },
          ],
          filtersDeterminer: `all`,
          name: SPECIAL_EVENTS.ALL_EVENTS,
          profileType: undefined,
          search: ``,
        },
        display: {},
        measurement: {
          math: PRIMITIVE_MATH_TYPES.UNIQUE,
          perUserAggregation: undefined,
          rolling: undefined,
          cumulative: false,
          property: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
    ],
    time: weeklyIntervalLast3Months,
  },
});

export const retentionBookmark = (
  dataGroupId: string,
  distinctId: string,
  retentionUnit: TimeUnit,
  retentionBucketIndex: number,
  timeParams?: Array<{dateRangeType: DateRangeType; unit: TimeUnit; window: {unit: TimeUnit; value: number}}>,
): InsightsBookmarkParams => ({
  ...commonInsightsBookmarkParams,
  sections: {
    cohorts: [],
    filter: dataGroupFilter(dataGroupId, distinctId),
    formula: [],
    group: [],
    show: [
      {
        isExpanded: true,
        behavior: {
          type: MetricType.Retention,
          resourceType: InsightsResourceType.Events,
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          name: undefined,
          filters: [],
          behaviors: [
            {
              id: undefined,
              type: MetricType.Event,
              name: SPECIAL_EVENTS.ANY_EVENT,
              filters: [
                {
                  dataset: `$mixpanel`,
                  value: `$nth_time_event`,
                  resourceType: InsightsResourceType.Events,
                  profileType: null,
                  search: ``,
                  dataGroupId: undefined,
                  filterType: PropertyType.NonProperty,
                  defaultType: PropertyType.NonProperty,
                  filterOperator: NthTimeOperator.NthTime,
                  filterValue: null,
                  propertyObjectKey: undefined,
                },
              ],
              filtersDeterminer: `all`,
            },
            {
              id: undefined,
              type: MetricType.Event,
              name: SPECIAL_EVENTS.ANY_EVENT,
              filters: [],
              filtersDeterminer: `all`,
            },
          ],
          retentionType: `birth`,
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionUnit,
          retentionUnboundedMode: `none`,
          retentionCustomBucketSizes: [],
          profileType: undefined,
          search: ``,
        },
        display: {},
        measurement: {
          math: RETENTION_MATH_TYPES.RETENTION_RATE,
          perUserAggregation: undefined,
          rolling: undefined,
          cumulative: false,
          retentionCumulative: false,
          property: undefined,
          retentionBucketIndex,
          retentionSegmentationEvent: SPECIAL_EVENTS.ANY_EVENT,
          segmentMethod: undefined,
          multiAttribution: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
    ],
    time: timeParams ? timeParams : dailyIntervalLast30Days,
  },
});

export const weeklyNewUserBookmark = (dataGroupId: string, distinctId: string): InsightsBookmarkParams => ({
  ...commonInsightsBookmarkParams,
  sections: {
    cohorts: [],
    filter: [],
    formula: [],
    group: [],
    show: [
      {
        behavior: {
          type: MetricType.Event,
          resourceType: InsightsResourceType.Events,
          dataGroupId: undefined,
          dataset: `$mixpanel`,
          filters: [
            {
              dataset: `$mixpanel`,
              value: MPProperties.DistinctId,
              resourceType: InsightsResourceType.People,
              profileType: null,
              search: ``,
              dataGroupId,
              filterType: PropertyType.String,
              defaultType: PropertyType.String,
              filterOperator: StringOperator.Equals,
              filterValue: [distinctId],
              propertyObjectKey: null,
              determiner: `all`,
            },
            {
              dataset: `$mixpanel`,
              value: `$nth_time_event`,
              resourceType: InsightsResourceType.Events,
              profileType: null,
              search: ``,
              dataGroupId: undefined,
              filterType: PropertyType.NonProperty,
              defaultType: PropertyType.NonProperty,
              filterOperator: NthTimeOperator.NthTime,
              filterValue: undefined,
              propertyObjectKey: undefined,
            },
          ],
          filtersDeterminer: `all`,
          name: SPECIAL_EVENTS.ALL_EVENTS,
          profileType: undefined,
          search: ``,
        },
        display: {},
        measurement: {
          math: XAU_MATH_TYPES.WAU,
          perUserAggregation: undefined,
          rolling: undefined,
          cumulative: false,
          property: undefined,
          segmentMethod: undefined,
        },
        type: TopLevelMetricType.Metric,
      },
    ],
    time: weeklyIntervalLast3Months,
  },
});

const userNameProperty = {
  label: `Name`,
  propertyDefaultType: PropertyType.String,
  resourceType: ResourceType.User,
  type: PropertyType.String,
  value: `$name`,
  dataGroupId: ``,
};

const userEmailProperty = {
  label: `Email`,
  propertyDefaultType: PropertyType.String,
  resourceType: ResourceType.User,
  type: PropertyType.String,
  value: `$email`,
  dataGroupId: ``,
};

export const DISTINCT_ID_LABEL = `Distinct ID`;
const userDistinctIdProperty = {
  label: DISTINCT_ID_LABEL,
  propertyDefaultType: PropertyType.String,
  resourceType: ResourceType.User,
  type: PropertyType.String,
  value: MPProperties.DistinctId,
};

const timeProperty = {
  label: `Time`,
  propertyDefaultType: `datetime`,
  resourceType: ResourceType.Event,
  type: `datetime`,
  unit: `hour`,
  value: `$time`,
};

enum UserFeedCustomPropertyName {
  TotalNumberOfEvents = `Total Number of Events`,
  FirstEventTime = `First event Time (Hour)`,
  MostActiveHour = `Most Active hour`,
}

const totalNumberOfEventsCustomProperty = {
  customProperty: {
    behavior: {
      aggregationOperator: `total`,
      dateRange: {
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: 30,
        },
      },
      event: {
        label: SPECIAL_EVENTS.ANY_EVENT,
        value: SPECIAL_EVENTS.ANY_EVENT,
      },
      filters: [],
      filtersOperator: `and`,
      property: null,
    },
    description: ``,
    name: UserFeedCustomPropertyName.TotalNumberOfEvents,
    resourceType: InsightsResourceType.Events,
    propertyType: PropertyType.Number,
  },
  customPropertyId: `$temp-SqBtqyNGm8FvYeR2IJav6`,
  resourceType: ResourceType.Event,
  type: PropertyType.Number,
  value: UserFeedCustomPropertyName.TotalNumberOfEvents,
  dataGroupId: null,
  label: UserFeedCustomPropertyName.TotalNumberOfEvents,
};

export const powerUserBookmarkParams = (dataGroupId: string, distinctId: string) => ({
  chosenColumns: [userNameProperty, userEmailProperty, userDistinctIdProperty, totalNumberOfEventsCustomProperty],
  columnWidths: {
    $distinct_id: 200,
    $email: 244,
  },
  filterByCohort: {
    name: ``,
    id: null,
    unsavedId: null,
    groups: [
      {
        behavioralFiltersOperator: `and`,
        event: {
          resourceType: ResourceType.Cohort,
          value: PredefinedCohort.AllUsers,
        },
        filters: [
          {
            customProperty: {
              name: ``,
              description: ``,
              behavior: {
                aggregationOperator: `total`,
                aggregationOperatorPerUser: null,
                behavioralFiltersOperator: `and`,
                dateRange: {
                  type: DateRangeType.RelativeAfter,
                  window: {
                    value: 30,
                    unit: TimeUnit.Day,
                  },
                },
                event: {
                  value: SPECIAL_EVENTS.ANY_EVENT,
                  label: `Any event`,
                  isRecentlyUsed: false,
                  icon: ICON_MAPPING[MetricType.CustomEvent],
                },
                filters: [
                  {
                    filterValue: [distinctId],
                    dataGroupId,
                    resourceType: ResourceType.User,
                    propertyName: MPProperties.DistinctId,
                    propertyObjectKey: null,
                    propertyDefaultType: PropertyType.String,
                    propertyType: PropertyType.String,
                    filterOperator: StringOperator.Equals,
                  },
                ],
                filtersOperator: `and`,
                property: null,
              },
            },
            customPropertyId: null,
            dataGroupId: null,
            tempDataGroupId: null,
            resourceType: ResourceType.User,
            propertyName: null,
            propertyObjectKey: null,
            propertyDefaultType: PropertyType.Number,
            propertyType: PropertyType.Number,
            filterOperator: NumberOperator.AtLeast,
            filterValue: 10,
          },
        ],
        filtersOperator: `and`,
        groupingOperator: null,
        property: null,
        type: `cohort_group`,
      },
    ],
  },
  includeAllUsers: true,
  sortOrder: `descending`,
  sortProperty: totalNumberOfEventsCustomProperty,
  sortPropertyType: `datetime`,
});

const firstEventTimeCustomProperty = {
  customProperty: {
    behavior: {
      aggregationOperator: `first_value`,
      dateRange: {
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: 30,
        },
      },
      event: {
        label: SPECIAL_EVENTS.ANY_EVENT,
        value: SPECIAL_EVENTS.ANY_EVENT,
      },
      filters: [
        {
          filterOperator: NthTimeOperator.NthTime,
          filterValue: null,
          propertyDefaultType: `other`,
          propertyName: `$nth_time_event`,
          propertyType: `other`,
          resourceType: ResourceType.Event,
        },
      ],
      filtersOperator: `and`,
      property: timeProperty,
    },
    description: ``,
    name: UserFeedCustomPropertyName.FirstEventTime,
    propertyType: PropertyType.String,
    resourceType: InsightsResourceType.Events,
  },
  customPropertyId: `$temp-kMMRwkj-kQOB7T-hRwme-`,
  label: UserFeedCustomPropertyName.FirstEventTime,
  propertyDefaultType: PropertyType.String,
  resourceType: ResourceType.User,
  type: PropertyType.String,
  value: UserFeedCustomPropertyName.FirstEventTime,
};

export const newUserBookmarkParams = (dataGroupId: string, distinctId: string) => ({
  chosenColumns: [userNameProperty, userEmailProperty, userDistinctIdProperty, firstEventTimeCustomProperty],
  columnWidths: {
    $distinct_id: 242,
    $email: 188,
    $name: 166,
  },
  filterByCohort: {
    groups: [
      {
        behavioralFiltersOperator: `and`,
        event: {
          resourceType: ResourceType.Cohort,
          value: PredefinedCohort.AllUsers,
        },
        filters: [
          {
            customProperty: {
              behavior: {
                aggregationOperator: `total`,
                aggregationOperatorPerUser: null,
                behavioralFiltersOperator: `and`,
                dateRange: {
                  type: DateRangeType.RelativeAfter,
                  window: {
                    unit: TimeUnit.Day,
                    value: 30,
                  },
                },
                event: {
                  icon: ICON_MAPPING[MetricType.CustomEvent],
                  isRecentlyUsed: false,
                  label: `Any event`,
                  value: SPECIAL_EVENTS.ANY_EVENT,
                },
                filters: [
                  {
                    dataGroupId,
                    filterOperator: StringOperator.Equals,
                    filterValue: [distinctId],
                    propertyDefaultType: PropertyType.String,
                    propertyName: MPProperties.DistinctId,
                    propertyObjectKey: null,
                    propertyType: PropertyType.String,
                    resourceType: ResourceType.User,
                  },
                  {
                    filterOperator: NthTimeOperator.NthTime,
                    filterValue: null,
                    propertyDefaultType: `other`,
                    propertyName: `$nth_time_event`,
                    propertyType: `other`,
                    resourceType: ResourceType.Event,
                  },
                ],
                filtersOperator: `and`,
                property: null,
              },
              description: ``,
              name: ``,
            },
            customPropertyId: null,
            dataGroupId: null,
            filterOperator: NumberOperator.AtLeast,
            filterValue: 1,
            propertyDefaultType: PropertyType.Number,
            propertyName: null,
            propertyObjectKey: null,
            propertyType: PropertyType.Number,
            resourceType: ResourceType.User,
            tempDataGroupId: null,
          },
        ],
        filtersOperator: `and`,
        groupingOperator: null,
        property: null,
        type: `cohort_group`,
      },
    ],
    id: null,
    name: ``,
    unsavedId: null,
  },
  includeAllUsers: true,
  sortOrder: `descending`,
  sortProperty: firstEventTimeCustomProperty,
  sortPropertyType: `datetime`,
});

const mostActiveHourCustomProperty = {
  customProperty: {
    behavior: {
      aggregationOperator: `most_frequent`,
      dateRange: {
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: 30,
        },
      },
      event: {
        label: SPECIAL_EVENTS.ANY_EVENT,
        value: SPECIAL_EVENTS.ANY_EVENT,
      },
      filters: [],
      filtersOperator: `and`,
      property: timeProperty,
    },
    description: ``,
    name: UserFeedCustomPropertyName.MostActiveHour,
    propertyType: PropertyType.String,
    resourceType: InsightsResourceType.Events,
  },
  customPropertyId: `$temp-U1xYknZM1uKSHQ8lELV1n`,
  dataGroupId: null,
  label: UserFeedCustomPropertyName.MostActiveHour,
  resourceType: ResourceType.Event,
  type: PropertyType.String,
  value: UserFeedCustomPropertyName.MostActiveHour,
};

export const recentUserBookmarkParams = (dataGroupId: string, distinctId: string) => ({
  chosenColumns: [userNameProperty, userEmailProperty, userDistinctIdProperty, mostActiveHourCustomProperty],
  columnWidths: {
    $distinct_id: 197,
    $email: 220,
  },
  filterByCohort: {
    groups: [
      {
        behavioralFiltersOperator: `and`,
        event: {
          resourceType: ResourceType.Cohort,
          value: PredefinedCohort.AllUsers,
        },
        filters: [
          {
            customProperty: {
              behavior: {
                aggregationOperator: `total`,
                aggregationOperatorPerUser: null,
                behavioralFiltersOperator: `and`,
                dateRange: {
                  type: DateRangeType.RelativeAfter,
                  window: {
                    unit: TimeUnit.Day,
                    value: 30,
                  },
                },
                event: {
                  icon: ICON_MAPPING[MetricType.CustomEvent],
                  isRecentlyUsed: false,
                  label: `Any event`,
                  value: SPECIAL_EVENTS.ANY_EVENT,
                },
                filters: [
                  {
                    dataGroupId,
                    filterOperator: StringOperator.Equals,
                    filterValue: [distinctId],
                    propertyDefaultType: PropertyType.String,
                    propertyName: MPProperties.DistinctId,
                    propertyObjectKey: null,
                    propertyType: PropertyType.String,
                    resourceType: ResourceType.User,
                  },
                ],
                filtersOperator: `and`,
                property: null,
              },
              description: ``,
              name: ``,
            },
            customPropertyId: null,
            dataGroupId: null,
            filterOperator: NumberOperator.AtLeast,
            filterValue: 1,
            propertyDefaultType: PropertyType.Number,
            propertyName: null,
            propertyObjectKey: null,
            propertyType: PropertyType.Number,
            resourceType: ResourceType.User,
            tempDataGroupId: null,
          },
        ],
        filtersOperator: `and`,
        groupingOperator: null,
        property: null,
        type: `cohort_group`,
      },
    ],
    id: null,
    name: ``,
    unsavedId: null,
  },
  includeAllUsers: true,
  sortOrder: `descending`,
  sortProperty: mostActiveHourCustomProperty,
  sortPropertyType: `datetime`,
});

export const userTabBookmarkParams = (dataGroupId: string, distinctId: string) => ({
  chosenColumns: [
    userNameProperty,
    userEmailProperty,
    {
      propertyDefaultType: PropertyType.Datetime,
      resourceType: ResourceType.User,
      type: PropertyType.Datetime,
      value: `$mp_first_event_time`,
      label: `First seen`,
    },
    {
      label: `Last seen`,
      propertyDefaultType: PropertyType.Datetime,
      resourceType: ResourceType.User,
      type: PropertyType.Datetime,
      value: `$last_seen`,
    },
    {
      label: `Country`,
      propertyDefaultType: PropertyType.String,
      resourceType: ResourceType.User,
      type: PropertyType.String,
      value: `country`,
    },
    {
      label: `Region`,
      propertyDefaultType: PropertyType.String,
      resourceType: ResourceType.User,
      type: PropertyType.String,
      value: `region`,
    },
  ],
  columnWidths: {},
  filterByCohort: {
    name: ``,
    id: null,
    unsavedId: null,
    groups: [
      {
        type: `cohort_group`,
        event: {
          resourceType: ResourceType.Cohort,
          value: `$all_users`,
        },
        filters: [
          {
            customProperty: {
              name: ``,
              description: ``,
              behavior: {
                aggregationOperator: `total`,
                aggregationOperatorPerUser: null,
                event: {
                  value: `$mp_anything_event`,
                  label: `Any event`,
                  isRecentlyUsed: false,
                },
                filters: [
                  {
                    filterValue: [distinctId],
                    dataGroupId,
                    resourceType: ResourceType.User,
                    propertyName: MPProperties.DistinctId,
                    propertyObjectKey: null,
                    propertyDefaultType: PropertyType.String,
                    propertyType: PropertyType.String,
                    filterOperator: StringOperator.Equals,
                  },
                ],
                filtersOperator: `and`,
                behavioralFiltersOperator: `and`,
                property: null,
                dateRange: {
                  type: DateRangeType.RelativeAfter,
                  window: {
                    value: 48,
                    unit: TimeUnit.Month,
                  },
                },
              },
            },
            customPropertyId: null,
            dataGroupId: null,
            tempDataGroupId: null,
            resourceType: ResourceType.User,
            propertyName: null,
            propertyObjectKey: null,
            propertyDefaultType: PropertyType.Number,
            propertyType: PropertyType.Number,
            filterOperator: NumberOperator.AtLeast,
            filterValue: 1,
          },
        ],
        filtersOperator: `and`,
        behavioralFiltersOperator: `and`,
        groupingOperator: null,
        property: null,
      },
    ],
  },
  sortOrder: `descending`,
  sortProperty: {
    label: `Updated at`,
    propertyDefaultType: PropertyType.Datetime,
    resourceType: ResourceType.User,
    type: PropertyType.Datetime,
    value: `$last_seen`,
  },
  sortPropertyType: PropertyType.Datetime,
});

export const usersPageGroupmodeParams = (dataGroupId: string) => ({
  chosenColumns: [
    {
      dataGroupId,
      label: DISTINCT_ID_LABEL,
      propertyDefaultType: `string`,
      resourceType: `user`,
      type: `string`,
      value: MPProperties.DistinctId,
    },
  ],
  columnWidths: {},
  dataGroupId,
  filterByCohort: {
    description: ``,
    name: ``,
    id: null,
    unsavedId: ``,
    groups: [
      {
        type: `cohort_group`,
        event: {
          resourceType: `cohort`,
          value: `$all_users`,
        },
        filters: [],
        filtersOperator: `and`,
        behavioralFiltersOperator: `and`,
        groupingOperator: null,
        property: null,
        dataGroupId,
      },
    ],
    data_group_id: dataGroupId,
  },
  sortOrder: `descending`,
  sortProperty: {
    dataGroupId,
    label: DISTINCT_ID_LABEL,
    propertyDefaultType: `string`,
    resourceType: `user`,
    type: `string`,
    value: MPProperties.DistinctId,
  },
  sortPropertyType: `datetime`,
});
